import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  clearAppSourceDetail,
  createAppSource,
  fetchAppSourceById,
  generateAppSourceSecret,
  resetSaveState,
  updateAppSource
} from '../store/slices/appSourcesSlice';

const statusOptions = ['ACTIVE', 'INACTIVE'];
const quotaTypeOptions = ['FARE', 'TICKET'];

const defaultFormValues = {
  name: '',
  status: 'ACTIVE',
  secret_key: '',
  callback_url: '',
  quota: 0,
  quota_type: 'FARE',
  start_date: '',
  end_date: ''
};

const AppSourceFormPage = ({ mode = 'create' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const rawEditingId = params.id ? Number(params.id) : null;
  const editingId = Number.isFinite(rawEditingId) ? rawEditingId : null;
  const isEditMode = mode === 'edit' || Boolean(editingId);

  const {
    detail,
    detailStatus,
    detailError,
    secretGenerationStatus,
    secretGenerationError
  } = useSelector((state) => state.appSources);

  const isGeneratingSecret = secretGenerationStatus === 'loading';

  const [formValues, setFormValues] = useState(defaultFormValues);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const formId = 'app-source-form';

  useEffect(() => {
    if (isEditMode) {
      if (editingId !== null) {
        setFormError('');
        dispatch(fetchAppSourceById(editingId));
      } else {
        setFormError('The requested App Source could not be found.');
      }
    } else {
      setFormValues({ ...defaultFormValues });
      setFormError('');
    }

    return () => {
      dispatch(clearAppSourceDetail());
      dispatch(resetSaveState());
    };
  }, [dispatch, isEditMode, editingId]);

  useEffect(() => {
    if (!isEditMode) {
      setFormValues({ ...defaultFormValues });
      return;
    }

    if (detailStatus === 'succeeded' && detail && detail.id === editingId) {
      setFormValues({
        name: detail.name || '',
        status: detail.status || 'ACTIVE',
        secret_key: detail.secret_key || '',
        callback_url: detail.callback_url || '',
        quota: Number.isFinite(Number(detail.quota)) ? Number(detail.quota) : 0,
        quota_type: detail.quota_type || 'FARE',
        start_date: detail.start_datetime ? String(detail.start_datetime).slice(0, 10) : '',
        end_date: detail.end_datetime ? String(detail.end_datetime).slice(0, 10) : ''
      });
      setFormError('');
    } else if (detailStatus === 'failed') {
      setFormError(detailError || 'The requested App Source could not be found.');
    }
  }, [isEditMode, detailStatus, detail, detailError, editingId]);

  const validate = () => {
    if (!formValues.name.trim()) {
      return 'Name is required.';
    }
    if (formValues.name.trim().length > 50) {
      return 'Name must be 50 characters or less.';
    }
    if (!statusOptions.includes(formValues.status)) {
      return 'Status must be either ACTIVE or INACTIVE.';
    }
    if (!formValues.secret_key || !formValues.secret_key.trim()) {
      return 'Secret key is required. Please generate one.';
    }
    if (!formValues.callback_url || !formValues.callback_url.trim()) {
      return 'Callback URL is required.';
    }
    if (!quotaTypeOptions.includes(formValues.quota_type)) {
      return 'Quota type is required.';
    }
    if (!formValues.start_date) {
      return 'Start date is required.';
    }
    if (!formValues.end_date) {
      return 'End date is required.';
    }
    if (formValues.start_date > formValues.end_date) {
      return 'End date must be on or after the start date.';
    }
    if (formValues.quota === '' || Number.isNaN(Number(formValues.quota))) {
      return 'Quota must be a number.';
    }
    return '';
  };

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'quota') {
      value = value === '' ? '' : Number(value);
    }
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateSecret = async () => {
    setSubmitError('');
    try {
      const secret = await dispatch(generateAppSourceSecret(64)).unwrap();
      setFormValues((prev) => ({ ...prev, secret_key: secret }));
    } catch (error) {
      // Error state handled via slice
    }
  };

  const copySecretKey = () => {
    if (!formValues.secret_key) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(formValues.secret_key).catch(() => {
        /* swallow clipboard errors */
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate();
    if (validation) {
      setSubmitError(validation);
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);

    const payload = {
      name: formValues.name.trim(),
      status: formValues.status,
      secret_key: formValues.secret_key.trim(),
      callback_url: formValues.callback_url.trim(),
      quota: Number(formValues.quota ?? 0),
      quota_type: formValues.quota_type,
      start_datetime: `${formValues.start_date} 00:00:00`,
      end_datetime: `${formValues.end_date} 23:59:59`
    };

    try {
      if (isEditMode && editingId !== null) {
        await dispatch(updateAppSource({ id: editingId, ...payload })).unwrap();
      } else {
        await dispatch(createAppSource(payload)).unwrap();
      }
      navigate('/app-sources', { replace: true });
    } catch (errorMessage) {
      setSubmitError(errorMessage || 'Unable to save the app source right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && detailStatus === 'loading' && !detail) {
    return (
      <Paper sx={{ p: 4 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading app source details...</Typography>
        </Stack>
      </Paper>
    );
  }

  if (formError) {
    return (
      <Paper sx={{ p: 4 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Not Found
          </Typography>
          <Typography color="text.secondary">{formError}</Typography>
          <Button variant="outlined" onClick={() => navigate('/app-sources')}>
            Back to list
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 3 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Update App Source' : 'Create App Source'}
          </Typography>
          <Typography color="text.secondary">
            {isEditMode
              ? 'Modify the configuration of an existing application source.'
              : 'Define configuration and quota for the new application source.'}
          </Typography>
        </Box>
      </Stack>

      <Paper
        component="form"
        id={formId}
        onSubmit={handleSubmit}
        sx={{
          p: { xs: 2.5, md: 4 },
          mx: 'auto',
          width: '100%',
          maxWidth: 780
        }}
      >
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Grid
            container
            rowSpacing={3}
            columnSpacing={{ xs: 0, md: 3 }}
            sx={{ '& .MuiGrid-item': { px: { xs: 0, md: 1.5 } } }}
          >
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={formValues.name}
                onChange={handleChange('name')}
                required
                fullWidth
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Status"
                value={formValues.status}
                onChange={handleChange('status')}
                required
                fullWidth
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Secret Key"
                value={formValues.secret_key}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate secret key">
                        <IconButton
                          aria-label="Generate secret key"
                          onClick={handleGenerateSecret}
                          disabled={isGeneratingSecret}
                          edge="end"
                        >
                          {isGeneratingSecret ? (
                            <CircularProgress size={16} />
                          ) : (
                            <AutorenewIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy to clipboard">
                        <span>
                          <IconButton
                            aria-label="Copy secret key"
                            onClick={copySecretKey}
                            disabled={!formValues.secret_key || isGeneratingSecret}
                            edge="end"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Callback URL"
                value={formValues.callback_url}
                onChange={handleChange('callback_url')}
                required
                fullWidth
                type="url"
                placeholder="https://partner.example.com/qr-ready"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Quota Type"
                value={formValues.quota_type}
                onChange={handleChange('quota_type')}
                required
                fullWidth
              >
                {quotaTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Quota"
                type="number"
                value={formValues.quota}
                onChange={handleChange('quota')}
                fullWidth
                helperText="Set 0 for unlimited quota"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={formValues.start_date}
                onChange={handleChange('start_date')}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={formValues.end_date}
                onChange={handleChange('end_date')}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {secretGenerationError && <Alert severity="error">{secretGenerationError}</Alert>}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="flex-end"
            spacing={1.5}
            sx={{ width: '100%' }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/app-sources')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {isSubmitting ? 'Submitting...' : isEditMode ? 'Save Changes' : 'Create'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default AppSourceFormPage;

